import type { PostgrestError } from '@supabase/supabase-js';

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { LearningType } from '@/lib/learnings/learning-candidate';
import type { ReadoutConfidenceTier } from '@/lib/readouts/readout-classification';

type WorkspaceLearningRow = Database['public']['Tables']['workspace_learnings']['Row'];

export type WorkspaceLearningStatus =
  'candidate' | 'active' | 'contested' | 'expired' | 'superseded' | 'rejected';

export interface WorkspaceLearning {
  id: string;
  ownerId: string;
  brandKitId: string;
  sourceReadoutId: string;
  learningType: LearningType;
  claim: string;
  scope: Record<string, unknown>;
  evidenceSummary: Record<string, unknown>;
  confidenceTier: ReadoutConfidenceTier;
  status: WorkspaceLearningStatus;
  validFrom: string;
  expiresAt: string | null;
  supersededBy: string | null;
  createdBy: string;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export function resolveWorkspaceLearningStatus(
  learning: Pick<WorkspaceLearning, 'status' | 'expiresAt'>,
  now = new Date(),
): WorkspaceLearningStatus {
  if (
    learning.status === 'active' &&
    learning.expiresAt &&
    new Date(learning.expiresAt).getTime() <= now.getTime()
  ) {
    return 'expired';
  }
  return learning.status;
}

export interface PromoteWorkspaceLearningInput {
  sourceReadoutId: string;
  learningType: LearningType;
  claim: string;
  scope?: Record<string, unknown>;
  evidenceSummary?: Record<string, unknown>;
  confidenceTier?: ReadoutConfidenceTier;
}

const WORKSPACE_LEARNING_COLUMNS =
  'id, owner_id, brand_kit_id, source_readout_id, learning_type, claim, scope_json, evidence_summary_json, confidence_tier, status, valid_from, expires_at, superseded_by, created_by, approved_by, approved_at, created_at, updated_at';

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function toWorkspaceLearning(row: WorkspaceLearningRow): WorkspaceLearning {
  return {
    id: row.id,
    ownerId: row.owner_id,
    brandKitId: row.brand_kit_id,
    sourceReadoutId: row.source_readout_id,
    learningType: row.learning_type as LearningType,
    claim: row.claim,
    scope: asRecord(row.scope_json),
    evidenceSummary: asRecord(row.evidence_summary_json),
    confidenceTier: row.confidence_tier as ReadoutConfidenceTier,
    status: row.status as WorkspaceLearningStatus,
    validFrom: row.valid_from,
    expiresAt: row.expires_at,
    supersededBy: row.superseded_by,
    createdBy: row.created_by,
    approvedBy: row.approved_by,
    approvedAt: row.approved_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

type PromoteWorkspaceLearningArgs = {
  p_source_readout_id: string;
  p_learning_type: string;
  p_claim: string;
  p_scope_json: Record<string, unknown>;
  p_evidence_summary_json: Record<string, unknown>;
  p_confidence_tier: string;
};

type PromoteWorkspaceLearningRpc = (
  fn: 'promote_workspace_learning',
  args: PromoteWorkspaceLearningArgs,
) => PromiseLike<{ data: WorkspaceLearningRow | null; error: PostgrestError | null }>;

/**
 * The three lifecycle-transition RPCs applied in
 * `20260829210131_harden_brief_learning_loop.sql`. Same
 * discriminated shape as `promote()`/`rpc.bind` above, and the same reason
 * for it: `supabase.rpc`'s generated overload set does not know about a
 * function this repo has not applied yet.
 */
type ContestWorkspaceLearningRpc = (
  fn: 'contest_workspace_learning',
  args: { p_learning_id: string },
) => PromiseLike<{ data: WorkspaceLearningRow | null; error: PostgrestError | null }>;

type ExpireWorkspaceLearningRpc = (
  fn: 'expire_workspace_learning',
  args: { p_learning_id: string; p_expires_at?: string },
) => PromiseLike<{ data: WorkspaceLearningRow | null; error: PostgrestError | null }>;

type SupersedeWorkspaceLearningRpc = (
  fn: 'supersede_workspace_learning',
  args: { p_learning_id: string; p_superseded_by: string },
) => PromiseLike<{ data: WorkspaceLearningRow | null; error: PostgrestError | null }>;

type AttachWorkspaceLearningToBriefRpc = (
  fn: 'attach_workspace_learning_to_brief',
  args: { p_brief_id: string; p_learning_id: string },
) => PromiseLike<{ data: { id: string } | null; error: PostgrestError | null }>;

function friendlyLifecycleError(action: string, error: PostgrestError): Error {
  if (error.code === '42501') {
    return new Error(`This learning could not be ${action} — it is unavailable to you.`);
  }
  if (error.code === '22023') {
    return new Error(`This learning's current status does not allow it to be ${action}.`);
  }
  if (error.code === '23514') {
    return new Error(`This ${action} request does not meet the table's rules.`);
  }
  return new Error(error.message);
}

/**
 * Mirrors `BriefReadoutService`'s `friendlyAcceptError` exactly — same two
 * codes matter here for the same reasons: `40001` never applies to an insert
 * (no optimistic-concurrency column on this RPC), but `42501` is the code
 * every provenance-trigger refusal in `enforce_workspace_learning_provenance`
 * raises (not-accepted source readout, workspace mismatch, no-workspace
 * readout, cross-workspace supersession), so it gets one honest sentence
 * instead of a raw Postgres error surfacing in the UI.
 */
function friendlyPromoteError(error: PostgrestError): Error {
  if (error.code === '42501') {
    return new Error(
      'This learning could not be promoted — its source readout must be accepted and scoped to this workspace.',
    );
  }
  if (error.code === '23514') {
    return new Error("This claim does not meet the table's length or format rules.");
  }
  return new Error(error.message);
}

export class WorkspaceLearningService {
  /**
   * Complete learning history for one or more source readouts. Unlike
   * `listActive`, this is an audit/read model: an expired, contested, or
   * superseded learning remains visible on the Brief that produced it.
   */
  async listForReadouts(sourceReadoutIds: string[]): Promise<WorkspaceLearning[]> {
    const ids = [...new Set(sourceReadoutIds.filter(Boolean))];
    if (ids.length === 0) return [];

    const { data, error } = await supabase
      .from('workspace_learnings')
      .select(WORKSPACE_LEARNING_COLUMNS)
      .in('source_readout_id', ids)
      .order('updated_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => toWorkspaceLearning(row as WorkspaceLearningRow));
  }

  /**
   * Every ACTIVE learning for a workspace — the retrieval surface a Brief or
   * an agent turn reads before drafting new work. Row-level security already
   * narrows to `owner_id = current_account_owner_id()` AND
   * `brand_kit_id IN my_workspace_ids()`
   * (`workspace_learnings_owner_workspace_select`), so this does not
   * hand-write an owner filter — same reasoning as
   * `BriefReadoutService.listOpen`.
   */
  async listActive(brandKitId: string): Promise<WorkspaceLearning[]> {
    const nowIso = new Date().toISOString();
    const { data, error } = await supabase
      .from('workspace_learnings')
      .select(WORKSPACE_LEARNING_COLUMNS)
      .eq('brand_kit_id', brandKitId)
      .eq('status', 'active')
      .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
      .order('updated_at', { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => toWorkspaceLearning(row as WorkspaceLearningRow));
  }

  /**
   * The most recent learnings promoted for a workspace, ANY status — an
   * activity feed's source, not the "what applies right now" retrieval
   * surface `listActive` is. A learning that later expired or was superseded
   * was still a real thing Mora promoted at the time, and a feed reporting
   * "what happened" must keep saying so even after the learning's current
   * applicability changes; `listActive`'s narrower `status = 'active'` filter
   * would make a promoted-then-superseded learning silently vanish from a
   * history that already showed it once. Same RLS narrowing as `listActive`
   * (`workspace_learnings_owner_workspace_select`).
   */
  async listRecent(limit = 25, brandKitId?: string | null): Promise<WorkspaceLearning[]> {
    let query = supabase
      .from('workspace_learnings')
      .select(WORKSPACE_LEARNING_COLUMNS)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (brandKitId) query = query.eq('brand_kit_id', brandKitId);
    const { data, error } = await query;
    if (error) throw new Error(error.message);
    return (data ?? []).map((row) => toWorkspaceLearning(row as WorkspaceLearningRow));
  }

  async promote(input: PromoteWorkspaceLearningInput): Promise<WorkspaceLearning> {
    const rpc = supabase.rpc.bind(supabase) as unknown as PromoteWorkspaceLearningRpc;
    const { data, error } = await rpc('promote_workspace_learning', {
      p_source_readout_id: input.sourceReadoutId,
      p_learning_type: input.learningType,
      p_claim: input.claim,
      p_scope_json: input.scope ?? {},
      p_evidence_summary_json: input.evidenceSummary ?? {},
      p_confidence_tier: input.confidenceTier ?? 'low',
    });
    if (error) throw friendlyPromoteError(error);
    if (!data) throw new Error('Promoting the learning returned no result.');
    return toWorkspaceLearning(data);
  }

  async attachToBrief(briefId: string, learningId: string): Promise<string> {
    const rpc = supabase.rpc.bind(supabase) as unknown as AttachWorkspaceLearningToBriefRpc;
    const { data, error } = await rpc('attach_workspace_learning_to_brief', {
      p_brief_id: briefId,
      p_learning_id: learningId,
    });
    if (error) throw friendlyLifecycleError('attached', error);
    if (!data?.id) throw new Error('Attaching the learning returned no receipt.');
    return data.id;
  }

  /** See the "AUTHORED, NOT APPLIED" note on the RPC types above. */
  async contest(learningId: string): Promise<WorkspaceLearning> {
    const rpc = supabase.rpc.bind(supabase) as unknown as ContestWorkspaceLearningRpc;
    const { data, error } = await rpc('contest_workspace_learning', { p_learning_id: learningId });
    if (error) throw friendlyLifecycleError('contested', error);
    if (!data) throw new Error('Contesting the learning returned no result.');
    return toWorkspaceLearning(data);
  }

  /** See the "AUTHORED, NOT APPLIED" note on the RPC types above. */
  async expire(learningId: string, expiresAt?: string): Promise<WorkspaceLearning> {
    const rpc = supabase.rpc.bind(supabase) as unknown as ExpireWorkspaceLearningRpc;
    const { data, error } = await rpc('expire_workspace_learning', {
      p_learning_id: learningId,
      ...(expiresAt ? { p_expires_at: expiresAt } : {}),
    });
    if (error) throw friendlyLifecycleError('expired', error);
    if (!data) throw new Error('Expiring the learning returned no result.');
    return toWorkspaceLearning(data);
  }

  /** See the "AUTHORED, NOT APPLIED" note on the RPC types above. */
  async supersede(learningId: string, supersededBy: string): Promise<WorkspaceLearning> {
    const rpc = supabase.rpc.bind(supabase) as unknown as SupersedeWorkspaceLearningRpc;
    const { data, error } = await rpc('supersede_workspace_learning', {
      p_learning_id: learningId,
      p_superseded_by: supersededBy,
    });
    if (error) throw friendlyLifecycleError('superseded', error);
    if (!data) throw new Error('Superseding the learning returned no result.');
    return toWorkspaceLearning(data);
  }
}

export const workspaceLearningService = new WorkspaceLearningService();
